import { useState, useEffect, useRef } from "react";

// ═══════════════════════════════════════════════════════════════
// DESIGN SYSTEM — FLIPPER AESTHETIC
// ═══════════════════════════════════════════════════════════════
const C = {
  ivory: "#FFF9EE", chrome: "#E8E4DC", chromeShine: "#FFFFFF",
  chromeDark: "#B8B0A0", red: "#CC1122", redLight: "#FF2233",
  yellow: "#FFD000", yellowDark: "#E6A800", blue: "#0044BB",
  blueLight: "#0066FF", green: "#00AA44", orange: "#FF6600",
  black: "#1A1109", steel: "#889AAA", bump1: "#FF3344",
  bump2: "#FFCC00", bump3: "#0055CC",
};
const Ff = {
  display: "'Bangers', cursive",
  body: "'Oswald', sans-serif",
  mono: "'Share Tech Mono', monospace",
};
const GOOGLE_FONTS = `@import url('https://fonts.googleapis.com/css2?family=Bangers&family=Oswald:wght@300;400;600;700&family=Share+Tech+Mono&display=swap');`;

// ═══════════════════════════════════════════════════════════════
// SESSIONS DATA
// ═══════════════════════════════════════════════════════════════
const SESSIONS = [
  {
    id: "s1", num: 1, title: "Marché Gaming FR",
    emoji: "📊", color: C.blue,
    desc: "Le marché français du jeu vidéo : acteurs, chiffres, tendances.",
    content: {
      blocs: [
        { titre: "Le marché en chiffres", texte: "En 2023, le marché français du jeu vidéo représente 5,6 milliards d'euros (SELL). La France est le 4e marché européen. 37 millions de joueurs actifs, soit 54% de la population. Le mobile capte 51% des revenus.", source: "SELL — Syndicat des Éditeurs de Logiciels de Loisirs, Bilan 2023", url: "https://www.sell.fr/news/bilan-marche-2023" },
        { titre: "Profil des joueurs", texte: "Âge moyen : 31 ans. 52% d'hommes, 48% de femmes. Les 15-24 ans jouent en moyenne 14h/semaine. Le gaming féminin est le segment le plus dynamique (+12% en 2 ans). Les seniors (50+) représentent désormais 27% des joueurs mobile.", source: "SELL / Médiamétrie — Les Français et le jeu vidéo 2023 (oct. 2023)", url: "https://www.sell.fr/sites/default/files/essentiel-jeu-video/ejv_octobre_2023.pdf" },
        { titre: "Les acteurs clés", texte: "Éditeurs : Ubisoft (leader FR), Gameloft. Distributeurs : Micromania-Zing. Platforms : PlayStation, Xbox, Nintendo, PC, Mobile. Streamers : Twitch + YouTube Gaming. Esport : Karmine Corp, Team Vitality.", source: "Ministère de la Culture / DEPS — Chiffres clés du jeu vidéo en France 2024", url: "https://www.culture.gouv.fr/Media/medias-creation-rapide/chiffres-cles-2024_deps_jeu-video_fiche.pdf" },
      ],
      chiffres: [{ val: "5,6 Md€", label: "marché FR 2023" }, { val: "37M", label: "joueurs actifs" }, { val: "54%", label: "de la population" }, { val: "31 ans", label: "âge moyen joueur" }],
    },
    junior: {
      type: "quiz",
      titre: "Quiz — Connaissance du marché",
      consigne: "15 questions sur le marché gaming français. Seuil de validation : 75%. Tu as 3 balles.",
      questions: [
        { q: "Quel est le chiffre d'affaires du marché gaming français en 2023 ?", choices: ["3,2 Md€", "5,6 Md€", "7,1 Md€", "4,4 Md€"], correct: 1 },
        { q: "Quelle part du marché le mobile représente-t-il ?", choices: ["28%", "39%", "51%", "63%"], correct: 2 },
        { q: "Combien y a-t-il de joueurs actifs en France ?", choices: ["22 millions", "30 millions", "37 millions", "45 millions"], correct: 2 },
        { q: "Quel est le rang de la France en Europe ?", choices: ["2e", "3e", "4e", "5e"], correct: 2 },
        { q: "Quel est l'âge moyen d'un joueur français ?", choices: ["24 ans", "28 ans", "31 ans", "36 ans"], correct: 2 },
        { q: "Quelle part représentent les femmes parmi les joueurs ?", choices: ["31%", "40%", "48%", "55%"], correct: 2 },
        { q: "Quel éditeur français est leader sur le marché ?", choices: ["Gameloft", "Ubisoft", "Focus Entertainment", "Microids"], correct: 1 },
        { q: "Combien d'heures jouent en moyenne les 15-24 ans par semaine ?", choices: ["8h", "11h", "14h", "18h"], correct: 2 },
        { q: "Quel organisme publie le bilan annuel du marché français ?", choices: ["Médiamétrie", "SELL", "Alliance Digitale", "IDATE"], correct: 1 },
        { q: "Quel segment gaming féminin connaît la plus forte croissance ?", choices: ["PC gaming", "Console", "Mobile", "Esport"], correct: 2 },
        { q: "Karmine Corp est un acteur de quelle filière ?", choices: ["Édition de jeux", "Streaming", "Esport", "Distribution"], correct: 2 },
        { q: "Quelle plateforme de streaming gaming domine le marché ?", choices: ["YouTube Gaming", "Twitch", "Facebook Gaming", "TikTok Live"], correct: 1 },
        { q: "Les seniors (50+) représentent quelle part du gaming mobile ?", choices: ["12%", "19%", "27%", "34%"], correct: 2 },
        { q: "Micromania-Zing est positionnée comme :", choices: ["Éditeur", "Distributeur", "Développeur", "Agence media"], correct: 1 },
        { q: "Quelle organisation étudie les audiences gaming en France ?", choices: ["SELL", "Alliance Digitale", "Médiamétrie", "IAB France"], correct: 2 },
      ],
    },
    senior: {
      type: "infographie",
      titre: "Cartographie annotée du marché",
      consigne: "Construis une infographie annotée représentant la carte du marché gaming français : acteurs, segments, flux financiers, données-clés. Format : PDF, image ou Google Slides partagé. Ton travail sera évalué par l'IA sur 5 critères.",
      criteres: ["Exhaustivité des acteurs clés du marché", "Précision et actualité des données chiffrées", "Logique des flux et relations entre acteurs", "Clarté et lisibilité visuelle", "Originalité de la mise en forme"],
      exemplePrompt: "Indique ici ce que tu as représenté et les choix que tu as faits dans ton infographie...",
    },
  },
  {
    id: "s2", num: 2, title: "Formats pub in-game",
    emoji: "🎮", color: C.red,
    desc: "Les formats publicitaires dans les univers gaming : inventaire, efficacité, tendances.",
    content: {
      blocs: [
        { titre: "Les 6 grands formats in-game", texte: "Display in-game (bannières dans l'interface), Skinning de personnages / objets virtuels, Vidéo pre-roll / mid-roll (chargement, pause), Advergaming (jeu créé par la marque), Branded events (collaboration officielle Fortnite x Nike), Native in-game (panneaux publicitaires dans monde virtuel).", source: "Alliance Digitale — Livre blanc 'Publicité In-game : Marques, à vous de jouer !' (sept. 2023)", url: "https://www.alliancedigitale.org/alliance-digitale-annonce-la-sortie-du-livre-blanc-publicite-in-game-marques-a-vous-de-jouer/" },
        { titre: "Efficacité mesurée", texte: "Selon Anzu.io (2023), la publicité in-game affiche un taux de mémorisation de 44% vs 28% pour la publicité digitale classique. Le viewability atteint 99% sur formats intégrés au gameplay. Brand safety : environnement contrôlé = moins de problèmes que les réseaux sociaux.", source: "Alliance Digitale — Panorama Ecosystème Publicité & Gaming France 2022 (mémorisation, viewability)", url: "https://www.alliancedigitale.org/publication/panorama-ecosysteme-publicite-gaming-2022-en-france/" },
        { titre: "Tendances & disruption", texte: "Les NFTs et actifs virtuels brandés ouvrent un marché nouveau (skin Adidas dans Fortnite). L'IA générative permet la personnalisation dynamique des panneaux in-game selon le profil joueur. Le métavers maintient un intérêt même post-effervescence.", source: "PwC Global Entertainment & Media Outlook 2023-2027", url: "https://www.pwc.fr/fr/publications/medias-et-loisirs/gemo.html" },
      ],
      chiffres: [{ val: "44%", label: "mémorisation in-game" }, { val: "99%", label: "viewability intégré" }, { val: "+28%", label: "vs pub digitale classique" }, { val: "6", label: "formats principaux" }],
    },
    junior: {
      type: "patterns",
      titre: "Reconnaissance de formats",
      consigne: "Identifie le format publicitaire dans chaque description de scénario gaming. 10 situations. Seuil : 75%. Tu as 3 balles.",
      questions: [
        { q: "Dans GTA Online, une voiture Pegassi arbore le logo real-life d'Audi sur sa carrosserie.", choices: ["Display in-game", "Skinning de marque", "Branded event", "Advergaming"], correct: 1 },
        { q: "Avant de lancer une partie de mobile, une vidéo 30s pour une banque s'affiche avec bouton 'skip'.", choices: ["Pre-roll vidéo", "Display bannière", "Native in-game", "Mid-roll"], correct: 0 },
        { q: "Fortnite organise un concert virtuel de Travis Scott avec des skins exclusifs à l'effigie de l'artiste.", choices: ["Skinning", "Branded event", "Advergaming", "Display"], correct: 1 },
        { q: "Dans un jeu de football, des panneaux LED virtuels autour du terrain affichent des marques réelles.", choices: ["Native in-game", "Display bannière", "Skinning", "Pre-roll"], correct: 0 },
        { q: "Pepsi crée un mini-jeu de course sur mobile à ses couleurs, disponible gratuitement sur l'App Store.", choices: ["Branded event", "Advergaming", "Pre-roll", "Skinning"], correct: 1 },
        { q: "Pendant le temps de chargement d'un niveau, une publicité vidéo Nike de 15s s'affiche.", choices: ["Mid-roll", "Pre-roll", "Display", "Native"], correct: 0 },
        { q: "Dans The Sims, un meuble IKEA aux couleurs exactes du catalogue réel est disponible en jeu.", choices: ["Advergaming", "Native in-game", "Skinning", "Display"], correct: 1 },
        { q: "Sur Roblox, une marque de mode lance une salle d'exposition virtuelle permettant d'essayer ses vêtements sur son avatar.", choices: ["Branded event", "Pre-roll", "Native", "Display"], correct: 0 },
        { q: "En milieu de partie longue, une publicité interruptive de 6s pour McDonald's apparaît dans l'interface.", choices: ["Pre-roll", "Mid-roll", "Display", "Skinning"], correct: 1 },
        { q: "Dans FIFA, des équipementiers sponsorisent les maillots des équipes exactement comme dans la réalité.", choices: ["Native in-game", "Skinning", "Branded event", "Advergaming"], correct: 0 },
      ],
    },
    senior: {
      type: "ecrit",
      titre: "Brief créatif — Recommandation de formats",
      consigne: "Une marque de ta région cherche à investir dans le gaming. Rédige un brief créatif de 300 mots recommandant 2 formats publicitaires adaptés à leur cible, avec justification chiffrée. Évalué par l'IA sur 5 critères.",
      criteres: ["Pertinence des formats recommandés au regard de la cible", "Qualité de l'argumentation avec données chiffrées", "Cohérence budget / format / objectif", "Clarté et professionnalisme rédactionnel", "Audace et originalité de l'approche"],
      exemplePrompt: "Présente ta marque choisie, sa cible, puis argumente tes 2 recommandations de formats...",
    },
  },
  {
    id: "s3", num: 3, title: "Offre Gaming",
    emoji: "📦", color: C.green,
    desc: "Construire et valoriser une offre publicitaire gaming : packages, tarifs, argumentaire.",
    content: {
      blocs: [
        { titre: "Structurer une offre gaming", texte: "Une offre gaming se structure en 3 niveaux : Awareness (visibilité large, CPM entre 8€-15€), Engagement (interaction, CPE entre 0,5€-2€), Conversion (clic et achat, CPC entre 0,8€-3€). Les packages intégrés combinant formats sont la norme pour les budgets > 50k€.", source: "Alliance Digitale — Livre blanc publicité in-game 2023 (niveaux CPM et CPE gaming)", url: "https://www.alliancedigitale.org/alliance-digitale-annonce-la-sortie-du-livre-blanc-publicite-in-game-marques-a-vous-de-jouer/" },
        { titre: "Tarification et négociation", texte: "Les plateformes gaming premium (Twitch, Fortnite) pratiquent des minima à 20k€ pour des activations branded. Les CPM vidéo in-game gaming sont 30-50% supérieurs au display web classique, compensés par la mémorisation et l'attention supérieures.", source: "SRI / Oliver Wyman — Observatoire de l'e-pub, bilan publicité digitale France 2023", url: "https://www.sri-france.org/recherches/observatoire-de-le-pub/" },
        { titre: "L'argumentaire de vente", texte: "5 arguments top des régies gaming : 1) Attention qualité (session gaming = contexte actif vs scroll passif). 2) Brand safety maîtrisée. 3) Audience jeune et difficile à atteindre via TV. 4) Innovation = image de marque associée. 5) Données first-party enrichies du profil joueur.", source: "Alliance Digitale — Glossaire du gaming publicitaire : formats, argumentaires, KPIs (2024)", url: "https://www.alliancedigitale.org/publication/le-glossaire-du-gaming-publicitaire-dalliance-digitale-les-cles-pour-explorer-un-univers-en-pleine-expansion/" },
      ],
      chiffres: [{ val: "8-15€", label: "CPM awareness gaming" }, { val: "+50%", label: "CPM vs display classique" }, { val: "20k€", label: "minimum plateformes premium" }, { val: "5", label: "arguments régies top" }],
    },
    junior: {
      type: "quiz",
      titre: "Quiz + grille tarifaire",
      consigne: "12 questions sur l'offre gaming et la tarification. Seuil : 75%. 3 balles.",
      questions: [
        { q: "Quel est le CPM moyen pour un format Awareness gaming ?", choices: ["2-5€", "8-15€", "20-30€", "35-50€"], correct: 1 },
        { q: "Qu'est-ce que le CPE ?", choices: ["Coût par éditeur", "Coût par engagement", "Coût par effet", "Coût par écran"], correct: 1 },
        { q: "De combien le CPM vidéo gaming dépasse-t-il le display web classique ?", choices: ["10-15%", "20-25%", "30-50%", "60-80%"], correct: 2 },
        { q: "Quel est le budget minimum habituel pour une activation Twitch branded ?", choices: ["5k€", "10k€", "20k€", "50k€"], correct: 2 },
        { q: "Un package intégré combine généralement :", choices: ["Un seul format, plusieurs plateformes", "Plusieurs formats sur une plateforme", "Plusieurs formats sur plusieurs supports", "Un format et un influenceur"], correct: 2 },
        { q: "Lequel N'est PAS un argument clé de vente d'une offre gaming ?", choices: ["Attention qualité", "Brand safety maîtrisée", "Audience difficile à atteindre via TV", "Coût inférieur à la TV nationale"], correct: 3 },
        { q: "Le CPE se situe généralement entre :", choices: ["0,1€-0,3€", "0,5€-2€", "3€-6€", "8€-15€"], correct: 1 },
        { q: "Alliance Digitale publie quel type de référentiel ?", choices: ["Audience TV", "Publicité digitale", "Gaming exclusif", "Données mobiles"], correct: 1 },
        { q: "Les données first-party gaming sont particulièrement précieuses car :", choices: ["Elles sont moins chères que les données tiers", "Elles enrichissent le profil joueur", "Elles sont obligatoires par la loi", "Elles remplacent les cookies"], correct: 1 },
        { q: "Quel niveau correspond à l'objectif 'Conversion' ?", choices: ["CPM", "CPE", "CPC", "CPV"], correct: 2 },
        { q: "Pour un annonceur avec 200k€ de budget, quelle approche est recommandée ?", choices: ["Format unique pour maximiser fréquence", "Package intégré multi-formats", "Exclusivement mobile", "Uniquement esport"], correct: 1 },
        { q: "La brand safety en gaming est généralement :", choices: ["Plus risquée qu'en social media", "Comparable aux réseaux sociaux", "Mieux maîtrisée qu'en social media", "Non mesurable"], correct: 2 },
      ],
    },
    senior: {
      type: "deck",
      titre: "Mini-deck de vente",
      consigne: "Crée un deck de vente de 5 slides (PDF ou lien Google Slides partagé) pour présenter une offre gaming à un annonceur fictif de ton choix. Évalué par l'IA sur 5 critères.",
      criteres: ["Clarté du positionnement de l'offre", "Pertinence de la segmentation budget/format", "Qualité de l'argumentaire adapté à l'annonceur", "Impact visuel et professionnalisme du deck", "Originalité et audace de l'approche commerciale"],
      exemplePrompt: "Décris ton annonceur fictif, les grandes lignes de ton offre et les choix que tu as faits dans ton deck...",
    },
  },
  {
    id: "s4", num: 4, title: "Plateformes Gaming",
    emoji: "📡", color: C.orange,
    desc: "Étude de cas : Twitch, Fortnite, Roblox — audiences, formats, opportunités.",
    content: {
      blocs: [
        { titre: "Twitch", texte: "140M d'utilisateurs mensuels mondiaux. 2,5M de streamers simultanés en pic. En France : 11,5M de visiteurs uniques/mois (Médiamétrie, 2023). Core audience : 18-34 ans. Formats : spots pre-roll, display overlay, sponsoring de streamers, channel points campaigns. CPM moyen : 10-16€.", source: "SELL / Médiamétrie — Les Français et le jeu vidéo 2023 ; données plateformes streaming", url: "https://www.sell.fr/sites/default/files/essentiel-jeu-video/ejv_octobre_2023.pdf" },
        { titre: "Fortnite / Epic Games", texte: "500M de comptes enregistrés. Fortnite Creative 2.0 permet aux marques de créer leurs propres îles expérientielles (Balenciaga Island, Nike'sland). Les branded events (concerts Travis Scott : 45M de spectateurs en 2020) redéfinissent l'entertainment. CPM événementiel : 25-40€.", source: "AFJV — Fortnite Creative 2.0 : islands de marques, événements brandés et chiffres Epic (2023)", url: "https://afjv.com/news/11241_fortnite-chapitre-5-nouvelles-fonctionnalites.htm" },
        { titre: "Roblox", texte: "65,7M DAU en Q3 2023. 54% des utilisateurs < 13 ans (à noter pour conformité COPPA/RGPD). Core 13-17 ans en croissance. Les marques créent des 'Experiences' (Vans World, Gucci Garden). Rentabilité : monétisation via Robux. Audience premium brand-safe.", source: "The Media Leader FR — Roblox : audiences, DAU et publicité pour les marques françaises (2023)", url: "https://fr.themedialeader.com/roblox-le-media-de-plus-de-65-millions-dutilisateurs-quotidiens-ouvre-enfin-aux-marques-francaises/" },
      ],
      chiffres: [{ val: "11,5M", label: "Twitch FR/mois" }, { val: "500M", label: "comptes Fortnite" }, { val: "65,7M", label: "Roblox DAU" }, { val: "54%", label: "Roblox <13 ans" }],
    },
    junior: {
      type: "quiz",
      titre: "Quiz + Matching Plateformes",
      consigne: "12 questions sur Twitch, Fortnite et Roblox. Seuil : 75%. 3 balles.",
      questions: [
        { q: "Combien de visiteurs uniques Twitch enregistre-t-il en France par mois ?", choices: ["5,2M", "8,9M", "11,5M", "17M"], correct: 2 },
        { q: "Fortnite comptabilise combien de comptes enregistrés ?", choices: ["200M", "350M", "500M", "750M"], correct: 2 },
        { q: "Quel est le DAU de Roblox au Q3 2023 ?", choices: ["32M", "45M", "65,7M", "89M"], correct: 2 },
        { q: "Quelle marque a créé une 'île' expérientielle dans Fortnite ?", choices: ["Adidas", "Nike", "Puma", "New Balance"], correct: 1 },
        { q: "Le concert de Travis Scott dans Fortnite a attiré combien de spectateurs ?", choices: ["12M", "28M", "45M", "100M"], correct: 2 },
        { q: "Quelle part des utilisateurs Roblox a moins de 13 ans ?", choices: ["28%", "41%", "54%", "67%"], correct: 2 },
        { q: "Quel format publicitaire est spécifique à Twitch ?", choices: ["Branded island", "Channel points campaign", "Robux experience", "Fortnite Creative"], correct: 1 },
        { q: "Fortnite Creative 2.0 sert à :", choices: ["Créer des skins de marque", "Permettre aux marques de créer des îles", "Gérer des campagnes display", "Diffuser du pre-roll"], correct: 1 },
        { q: "Le CPM événementiel Fortnite est estimé à :", choices: ["5-10€", "10-16€", "25-40€", "50-80€"], correct: 2 },
        { q: "Sur Roblox, la monétisation des marques passe par :", choices: ["CPM display", "Robux", "CPC", "Abonnements"], correct: 1 },
        { q: "Quelle réglementation doit être respectée sur Roblox en raison du jeune public ?", choices: ["RGPD uniquement", "COPPA / RGPD", "Loi Sapin 2", "Code ICC"], correct: 1 },
        { q: "Gucci Garden est une expérience branded sur quelle plateforme ?", choices: ["Fortnite", "Minecraft", "Roblox", "GTA Online"], correct: 2 },
      ],
    },
    senior: {
      type: "audio",
      titre: "Analyse comparative audio",
      consigne: "Enregistre une analyse comparative de 2 minutes : Twitch vs Fortnite vs Roblox pour une catégorie d'annonceur que tu choisis (luxe, sport, alimentaire, banque...). Justifie quelle plateforme tu recommandes et pourquoi. Évalué par l'IA sur 5 critères.",
      criteres: ["Maîtrise des données par plateforme", "Pertinence du choix de catégorie annonceur", "Logique et structure de l'argumentation comparative", "Clarté de la recommandation finale", "Profondeur de l'analyse (données, cas réels cités)"],
      exemplePrompt: "Transcription ou notes de ton analyse audio (optionnel, pour compléter l'évaluation)...",
    },
  },
  {
    id: "s5", num: 5, title: "Sponsoring Esport",
    emoji: "🏆", color: C.red,
    desc: "Stratégies de sponsoring esport : acteurs, mécaniques, ROI, cas terrain.",
    content: {
      blocs: [
        { titre: "L'écosystème esport", texte: "Le marché esport mondial atteint 1,5 Md$ en 2023 (Newzoo). En France, Karmine Corp et Team Vitality sont les deux grandes structures. La Karmine Corp est valorisée à environ 40M€ (2023). Les audiences en live tournoi : League of Legends Worlds 2023 = 6,1M spectateurs simultanés peak.", source: "France Esports / Médiamétrie — Baromètre France Esports 2023 (publié oct. 2023)", url: "https://www.france-esports.org/barometre-france-esports-resultats-de-ledition-2023/" },
        { titre: "Les mécaniques de sponsoring", texte: "5 niveaux de partnership : Title sponsor (naming de l'équipe ou du tournoi), Jersey sponsor, Naming rights (arènes), Contenu branded (series documentaires), Activation digitale (campagnes sociales conjointes). Le retour sur investissement se mesure via EMV (Earned Media Value).", source: "Sport Buzz Business — Structure du business esport : niveaux de sponsoring et ROI marques", url: "https://www.sportbuzzbusiness.fr/comment-se-structure-le-business-esport-qui-vise-15-milliard-de-dollars-de-revenus-en-2023-rapport-newzoo.html" },
        { titre: "ROI et mesure", texte: "Étude BVA Sport (2023) : 63% des fans esport ont une opinion plus favorable d'une marque présente en esport. Le taux d'affinité sponsor-équipe atteint 71% sur les 18-24 ans. EMV moyen d'une activation Karmine Corp : 3x le montant investi. Risque principal : image liée aux controverses des structures.", source: "France Esports — Baromètre 2023 : affinité sponsors, ROI et engagement fans esport France", url: "https://www.france-esports.org/barometre-france-esports-resultats-de-ledition-2023/" },
      ],
      chiffres: [{ val: "1,5 Md$", label: "marché esport mondial" }, { val: "40M€", label: "valorisation Karmine Corp" }, { val: "6,1M", label: "viewers simultanés Worlds 2023" }, { val: "3x", label: "EMV activation KC" }],
    },
    junior: {
      type: "quiz",
      titre: "Quiz + Contrat de sponsoring",
      consigne: "12 questions sur l'écosystème esport et les stratégies de sponsoring. Seuil : 75%. 3 balles.",
      questions: [
        { q: "Quel est le marché esport mondial en 2023 ?", choices: ["800M$", "1,2 Md$", "1,5 Md$", "2,3 Md$"], correct: 2 },
        { q: "Combien est valorisée la Karmine Corp en 2023 ?", choices: ["15M€", "25M€", "40M€", "65M€"], correct: 2 },
        { q: "Combien de viewers simultanés pour le Worlds LoL 2023 ?", choices: ["2,1M", "4,4M", "6,1M", "9,8M"], correct: 2 },
        { q: "Quel indicateur mesure le retour sur investissement en esport ?", choices: ["KPI", "ROI standard", "EMV", "CTR"], correct: 2 },
        { q: "Le titre sponsor implique :", choices: ["Un logo sur le maillot", "Le naming de l'équipe ou du tournoi", "Une campagne sociale", "Un naming d'arène"], correct: 1 },
        { q: "Selon BVA Sport, quel % des fans esport ont une opinion plus favorable d'un sponsor ?", choices: ["41%", "53%", "63%", "78%"], correct: 2 },
        { q: "L'EMV d'une activation Karmine Corp est en moyenne :", choices: ["Égal au montant investi", "2x le montant", "3x le montant", "5x le montant"], correct: 2 },
        { q: "Quel est le principal risque du sponsoring esport ?", choices: ["Audience trop jeune", "Coûts trop élevés", "Image liée aux controverses", "Brand safety faible"], correct: 2 },
        { q: "Le taux d'affinité sponsor-équipe sur les 18-24 ans est de :", choices: ["48%", "61%", "71%", "85%"], correct: 2 },
        { q: "Newzoo est :", choices: ["Une équipe esport", "Un organisme de mesure d'audience esport", "Un tournoi international", "Une régie publicitaire"], correct: 1 },
        { q: "Lequel N'est PAS un niveau de sponsoring esport ?", choices: ["Title sponsor", "Jersey sponsor", "Display CPM", "Naming rights"], correct: 2 },
        { q: "L'autre grande structure esport française avec Karmine Corp est :", choices: ["G2 Esports", "Team Vitality", "Fnatic", "Natus Vincere"], correct: 1 },
      ],
    },
    senior: {
      type: "ecrit",
      titre: "Note de recommandation stratégique",
      consigne: "Tu es consultant. Rédige une note de recommandation stratégique (400 mots) pour une marque fictive qui envisage son premier investissement en esport. Justifie ta stratégie, le niveau de partnership recommandé et les indicateurs de succès. Évalué par l'IA.",
      criteres: ["Connaissance des mécaniques de sponsoring esport", "Adéquation marque / niveau de partnership recommandé", "Pertinence des KPIs proposés", "Clarté et structure de la note", "Audace et vision stratégique"],
      exemplePrompt: "Présente ta marque fictive et développe ta recommandation stratégique...",
    },
  },
  {
    id: "s6", num: 6, title: "Simulation Négociation",
    emoji: "🤝", color: C.blue,
    desc: "Mise en situation : négocier une campagne gaming avec un interlocuteur-annonceur simulé par IA.",
    content: {
      blocs: [
        { titre: "La négociation publicitaire gaming", texte: "La vente d'une offre gaming nécessite de maîtriser 4 étapes : Découverte (comprendre les objectifs de la marque), Pitch (présenter l'offre adaptée), Gestion des objections (budget, audience, ROI), Closing (contractualisation). Les interlocuteurs types : Directeur Marketing, Directeur Médias, Brand Manager, Responsable Digital.", source: "Cours BTS Communication — Techniques de vente et négociation commerciale", url: "#" },
        { titre: "Les objections fréquentes en gaming", texte: "Top 5 objections annonceurs : 1) 'L'audience gaming est trop jeune pour nous'. 2) 'Je ne suis pas sûr de la brand safety'. 3) 'C'est difficile à mesurer'. 4) 'Mon budget est limité à 10k€'. 5) 'Je préfère les médias traditionnels qui ont fait leurs preuves'.", source: "Alliance Digitale — Livre blanc publicité in-game : freins et objections des annonceurs (2023)", url: "https://www.alliancedigitale.org/alliance-digitale-annonce-la-sortie-du-livre-blanc-publicite-in-game-marques-a-vous-de-jouer/" },
        { titre: "Les 6 personas de négociation", texte: "6 interlocuteurs avec des profils, budgets et styles de négociation variés : de la Directrice Marketing confirmée (budget 150k€, exigeante) au Gérant de PME curieux (budget 8k€, novice). Chaque persona requiert une approche adaptée. Accès à la simulation ci-dessous.", source: "Dispositif pédagogique BTS Communication — MOOC Gaming & Pub", url: "#" },
      ],
      chiffres: [{ val: "4", label: "étapes de négociation" }, { val: "5", label: "objections fréquentes" }, { val: "6", label: "personas disponibles" }, { val: "∞", label: "scénarios possibles" }],
    },
    junior: {
      type: "negociation",
      titre: "Négociation guidée",
      consigne: "Mène une négociation complète avec un persona de ton choix. Le chatbot simule l'annonceur, tu joues le rôle du commercial. La transcription est auto-générée et évaluée. Seuil : 75%. 3 balles.",
      questions: [], // handled by chatbot
    },
    senior: {
      type: "replay",
      titre: "Replay annoté + stratégie alternative",
      consigne: "Après ta négociation Junior validée, soumets un replay annoté : identifie 3 pivots clés de ta négociation, explique tes choix et propose une stratégie alternative. Évalué par l'IA sur 5 critères.",
      criteres: ["Identification pertinente des 3 pivots clés", "Qualité de l'analyse rétrospective", "Pertinence de la stratégie alternative", "Maîtrise du vocabulaire commercial", "Capacité à se remettre en question"],
      exemplePrompt: "Décris tes 3 pivots clés et ta stratégie alternative...",
    },
  },
];

const BADGES = {
  sniper: { emoji: "🎯", label: "Sniper", desc: "100% sur un quiz Junior", condition: (p) => Object.values(p.sessions).some(s => s.juniorScore === 100) },
  stratege: { emoji: "💡", label: "Stratège", desc: "Toutes sessions Senior validées", condition: (p) => Object.keys(p.sessions).length === 6 && Object.values(p.sessions).every(s => s.seniorValidated) },
  grinder: { emoji: "🔁", label: "Grinder", desc: "3 séances repassées pour améliorer", condition: (p) => Object.values(p.sessions).filter(s => (s.juniorAttempts || 0) > 1).length >= 3 },
  broadcaster: { emoji: "🎤", label: "Broadcaster", desc: "Rendu audio Senior > 85/100", condition: (p) => Object.values(p.sessions).some(s => s.seniorScore >= 85 && s.seniorType === "audio") },
  negociateur: { emoji: "🃏", label: "Négociateur", desc: "S6 Senior validée", condition: (p) => p.sessions.s6?.seniorValidated },
};

const MAX_SCORE = 600; // 6 sessions × 100 pts

// ═══════════════════════════════════════════════════════════════
// STORAGE UTILITIES
// ═══════════════════════════════════════════════════════════════
const LOCAL_KEY = "mooc_gaming_profile";
const ADMIN_TOKEN = "admin_bts2024";

function loadProfile() {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return null;
}

function saveProfile(p) {
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(p)); } catch {}
}

function computeScore(sessions) {
  return Math.round(Object.values(sessions).reduce((acc, s) => {
    const jPts = s.juniorScore != null ? (s.juniorScore / 100) * 30 : 0;
    const sPts = s.seniorScore != null ? (s.seniorScore / 100) * 70 : 0;
    return acc + jPts + sPts;
  }, 0));
}

function computeBadges(profile) {
  return Object.entries(BADGES)
    .filter(([, meta]) => { try { return meta.condition(profile); } catch { return false; } })
    .map(([, meta]) => meta.emoji);
}

async function pushToLeaderboard(profile) {
  if (!window.storage) return;
  const score = computeScore(profile.sessions);
  const badges = computeBadges(profile);
  const key = `lb:${profile.pseudo.toLowerCase().replace(/\s/g, "_")}`;
  try {
    await window.storage.set(key, JSON.stringify({
      pseudo: profile.pseudo, avatar: profile.avatar, avatarColor: profile.avatarColor || "blue",
      score, badges,
      sessions: Object.fromEntries(
        Object.entries(profile.sessions).map(([k, v]) => [k, { juniorScore: v.juniorScore, seniorScore: v.seniorScore, juniorValidated: v.juniorValidated, seniorValidated: v.seniorValidated }])
      ),
      updatedAt: Date.now(),
    }), true);
  } catch {}
}

async function loadLeaderboard() {
  if (!window.storage) return [];
  try {
    const keys = await window.storage.list("lb:", true);
    const entries = await Promise.all((keys.keys || []).map(async (k) => {
      try {
        const r = await window.storage.get(k, true);
        return r ? JSON.parse(r.value) : null;
      } catch { return null; }
    }));
    return entries.filter(Boolean).sort((a, b) => b.score - a.score);
  } catch { return []; }
}

// ═══════════════════════════════════════════════════════════════
// CLAUDE API EVALUATION
// ═══════════════════════════════════════════════════════════════
async function evaluateWithAI(sessionTitle, level, criteria, submission, type) {
  // En production : appel via Netlify Function (clé API sécurisée côté serveur)
  // En développement local : appel direct (nécessite REACT_APP_ANTHROPIC_API_KEY dans .env)
  const isNetlify = window.location.hostname !== "localhost";
  const endpoint = isNetlify
    ? "/.netlify/functions/evaluate"
    : "https://api.anthropic.com/v1/messages";

  try {
    if (isNetlify) {
      // Via Netlify Function — clé API jamais exposée
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionTitle, level, criteria, submission, type }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();

    } else {
      // Développement local — appel direct (pour tests uniquement)
      const apiKey = process.env.REACT_APP_ANTHROPIC_API_KEY || "";
      const prompt = `Tu es un évaluateur pédagogique expert en BTS Communication et en publicité gaming.
Évalue ce rendu de niveau ${level === "senior" ? "SENIOR" : "JUNIOR"} pour "${sessionTitle}".
TYPE : ${type}
CRITÈRES (chacun /20) :
${criteria.map((c, i) => `${i + 1}. ${c}`).join("\n")}
PRODUCTION :
"""${submission}"""
Réponds UNIQUEMENT en JSON sans markdown :
{"scores":[s1,s2,s3,s4,s5],"total":n,"validated":bool,"feedback":"...","strengths":["..."],"improve":"..."}
Seuil validation : 70/100.`;
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-api-key": apiKey, "anthropic-version": "2023-06-01" },
        body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 1024, messages: [{ role: "user", content: prompt }] }),
      });
      const data = await res.json();
      const text = data.content?.map(b => b.text || "").join("") || "";
      return JSON.parse(text.replace(/```json|```/g, "").trim());
    }
  } catch (e) {
    console.error("evaluateWithAI error:", e);
    return {
      scores: [14, 14, 14, 14, 14], total: 70, validated: true,
      feedback: "Évaluation automatique indisponible. Score par défaut attribué. Contactez votre enseignant pour une révision.",
      strengths: ["Travail remis"], improve: "Vérifier la connexion et réessayer.",
      _fallback: true,
    };
  }
}

// ═══════════════════════════════════════════════════════════════
// UI PRIMITIVES
// ═══════════════════════════════════════════════════════════════
function BtnPrimary({ children, onClick, disabled, small }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      fontFamily: Ff.display, fontSize: small ? 13 : 16, letterSpacing: 1,
      padding: small ? "6px 14px" : "10px 22px",
      background: disabled ? C.chrome : `linear-gradient(135deg, ${C.red}, #AA0011)`,
      color: disabled ? C.steel : "#FFF",
      border: `2px solid ${disabled ? C.chromeDark : C.black}`,
      borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer",
      boxShadow: disabled ? "none" : "3px 3px 0 rgba(0,0,0,0.25)",
      transition: "all 0.15s",
    }}>{children}</button>
  );
}

function BtnSecondary({ children, onClick, active }) {
  return (
    <button onClick={onClick} style={{
      fontFamily: Ff.body, fontWeight: 700, fontSize: 13, letterSpacing: 1,
      padding: "7px 16px",
      background: active ? `linear-gradient(135deg, ${C.yellow}, ${C.yellowDark})` : C.chrome,
      color: C.black,
      border: `2px solid ${active ? C.black : C.chromeDark}`,
      borderRadius: 6, cursor: "pointer",
      boxShadow: active ? "2px 2px 0 rgba(0,0,0,0.2)" : "none",
      transition: "all 0.15s",
    }}>{children}</button>
  );
}

function ChromePanel({ children, style = {} }) {
  return (
    <div style={{
      background: C.ivory, border: `2px solid ${C.chromeDark}`,
      borderRadius: 12, padding: 20,
      boxShadow: "4px 4px 0 rgba(0,0,0,0.1)",
      ...style,
    }}>{children}</div>
  );
}

function SevenSeg({ value, digits = 6, size = 22 }) {
  return (
    <span style={{
      fontFamily: Ff.mono, fontSize: size, letterSpacing: 3,
      color: C.red, textShadow: `0 0 8px ${C.red}66`,
      background: "#111", padding: "2px 8px", borderRadius: 4,
      border: `2px solid ${C.chromeDark}`,
    }}>{String(value).padStart(digits, "0")}</span>
  );
}

function BallsIndicator({ remaining, total = 3 }) {
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
      <span style={{ fontFamily: Ff.body, fontSize: 11, color: C.steel, letterSpacing: 1 }}>BALLES</span>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          width: 16, height: 16, borderRadius: "50%",
          background: i < remaining
            ? `radial-gradient(circle at 35% 30%, ${C.yellowLight || "#FFE840"}, ${C.yellow})`
            : C.chrome,
          border: `2px solid ${i < remaining ? C.yellow : C.chromeDark}`,
          boxShadow: i < remaining ? `0 0 6px ${C.yellow}88` : "none",
          transition: "all 0.3s",
        }} />
      ))}
    </div>
  );
}

function FlipperHeader({ currentView, setView, profile }) {
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    const i = setInterval(() => setFlash(v => !v), 1100);
    return () => clearInterval(i);
  }, []);
  const score = profile ? computeScore(profile.sessions) : 0;
  return (
    <div style={{
      background: `linear-gradient(180deg, ${C.chromeDark} 0%, ${C.chrome} 40%, ${C.chromeShine} 55%, ${C.chrome} 70%, ${C.chromeDark} 100%)`,
      padding: "10px 20px",
      borderBottom: `4px solid ${C.black}`,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      position: "sticky", top: 0, zIndex: 200,
      boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
    }}>
      <div style={{ textAlign: "center", minWidth: 130 }}>
        <div style={{ fontFamily: Ff.body, fontSize: 9, fontWeight: 700, color: C.red, letterSpacing: 2 }}>SCORE</div>
        <SevenSeg value={score} size={18} />
      </div>

      <div style={{ textAlign: "center", cursor: "pointer" }} onClick={() => setView("home")}>
        <div style={{
          fontFamily: Ff.display, fontSize: "clamp(18px,3vw,30px)",
          color: flash ? C.red : C.blue, letterSpacing: 2,
          textShadow: `2px 2px 0 ${C.black}`, transition: "all 0.3s", lineHeight: 1,
        }}>IT'S MORE FUN</div>
        <div style={{
          fontFamily: Ff.display, fontSize: "clamp(11px,1.8vw,18px)",
          color: C.yellow, letterSpacing: 5, textShadow: `1px 1px 0 ${C.black}`,
        }}>TO COMPETE</div>
        <div style={{ fontFamily: Ff.body, fontSize: 8, color: C.steel, letterSpacing: 3 }}>BTS COMMUNICATION — GAMING & PUB</div>
      </div>

      <div style={{ display: "flex", gap: 6, flexDirection: "column", alignItems: "flex-end" }}>
        {profile && (
          <div style={{ display: "flex", gap: 5, alignItems: "center", marginBottom: 3 }}>
            <AvatarBubble emoji={profile.avatar} colorKey={profile.avatarColor || "blue"} size={28} fontSize={16} />
            <span style={{ fontFamily: Ff.display, fontSize: 14, color: C.black }}>{profile.pseudo}</span>
          </div>
        )}
        <div style={{ display: "flex", gap: 4 }}>
          {[
            { key: "home", label: "📚 SÉANCES" },
            { key: "room", label: "🏆 SALLE" },
          ].map(tab => (
            <button key={tab.key} onClick={() => setView(tab.key)} style={{
              fontFamily: Ff.display, fontSize: 11, letterSpacing: 1,
              padding: "4px 10px",
              background: currentView === tab.key ? `linear-gradient(135deg, ${C.yellow}, ${C.yellowDark})` : "transparent",
              color: C.black, border: `2px solid ${currentView === tab.key ? C.black : C.chromeDark}`,
              borderRadius: 6, cursor: "pointer",
            }}>{tab.label}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ONBOARDING — AVATAR SETUP
// ═══════════════════════════════════════════════════════════════
// 40 emojis — couverture large pour 35 étudiants
const AVATARS = [
  // Animaux sauvages
  "🦁", "🦊", "🐺", "🐯", "🦅", "🐸", "🐻", "🦄",
  "🐼", "🦋", "🐲", "🦈", "🐬", "🦉", "🦎", "🐙",
  // Créatures & fantastiques
  "👾", "🤖", "👻", "🐉", "🦸", "🧙", "🥷", "🦾",
  // Gaming & tech
  "🎮", "🕹️", "🚀", "⚡", "🛸", "🎯", "🔥", "💎",
  // Personnages & fun
  "🦊", "🐧", "🦩", "🐊", "🦦", "🐺", "🦝", "🪄",
];
// Dédoublonnage (🦊 et 🐺 apparaissent 2× intentionnellement — filtrés ci-dessous)
const AVATARS_UNIQUE = [...new Set(AVATARS)];

// 8 couleurs de fond — combinaisons = 40 × 8 = 320 identités uniques
const AVATAR_COLORS = [
  { key: "red",    label: "Rouge",   bg: "#CC1122", light: "#FF4455" },
  { key: "blue",   label: "Bleu",    bg: "#0044BB", light: "#3377FF" },
  { key: "green",  label: "Vert",    bg: "#00AA44", light: "#00DD66" },
  { key: "yellow", label: "Jaune",   bg: "#E6A800", light: "#FFD000" },
  { key: "orange", label: "Orange",  bg: "#DD5500", light: "#FF7722" },
  { key: "purple", label: "Violet",  bg: "#7722BB", light: "#AA44EE" },
  { key: "teal",   label: "Cyan",    bg: "#007799", light: "#00AACC" },
  { key: "pink",   label: "Rose",    bg: "#CC2277", light: "#FF44AA" },
];

// Affichage unifié d'un avatar (emoji + couleur de fond)
function AvatarBubble({ emoji, colorKey, size = 44, fontSize = 24 }) {
  const col = AVATAR_COLORS.find(c => c.key === colorKey) || AVATAR_COLORS[0];
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize,
      background: `radial-gradient(circle at 35% 30%, ${col.light}, ${col.bg})`,
      border: `2.5px solid ${col.bg}`,
      boxShadow: `0 0 8px ${col.bg}55, inset 0 2px 4px rgba(255,255,255,0.3)`,
    }}>{emoji}</div>
  );
}

function Onboarding({ onComplete, existingCombos = [] }) {
  const [pseudo, setPseudo] = useState("");
  const [avatar, setAvatar] = useState("🦁");
  const [colorKey, setColorKey] = useState("blue");

  const combo = `${avatar}:${colorKey}`;
  const isTaken = existingCombos.includes(combo);
  const canSubmit = pseudo.trim() && !isTaken;

  return (
    <div style={{
      minHeight: "100vh", background: C.ivory,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }}>
      <ChromePanel style={{ maxWidth: 540, width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontFamily: Ff.display, fontSize: 32, color: C.black, letterSpacing: 2 }}>
            CRÉER TON PROFIL
          </div>
          <div style={{ fontFamily: Ff.body, fontSize: 13, color: C.steel, marginTop: 4 }}>
            Ton pseudo et ton avatar seront visibles dans la salle commune
          </div>
        </div>

        {/* Pseudo */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: Ff.display, fontSize: 16, color: C.black, marginBottom: 8, letterSpacing: 1 }}>
            TON PSEUDO
          </div>
          <input
            value={pseudo}
            onChange={e => setPseudo(e.target.value)}
            placeholder="Ex: LaserKing, PixelQueen..."
            maxLength={20}
            style={{
              width: "100%", fontFamily: Ff.body, fontSize: 16,
              padding: "10px 14px", border: `2px solid ${C.chromeDark}`,
              borderRadius: 8, background: C.chrome, color: C.black,
              outline: "none", boxSizing: "border-box",
            }}
          />
        </div>

        {/* Aperçu avatar */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
          <div style={{ textAlign: "center" }}>
            <AvatarBubble emoji={avatar} colorKey={colorKey} size={72} fontSize={38} />
            <div style={{ fontFamily: Ff.display, fontSize: 14, color: C.black, marginTop: 6, letterSpacing: 1 }}>
              {pseudo || "Ton pseudo"}
            </div>
            {isTaken && (
              <div style={{ fontFamily: Ff.body, fontSize: 11, color: C.red, marginTop: 4 }}>
                ⚠️ Cette combinaison est déjà prise — change l'emoji ou la couleur
              </div>
            )}
          </div>
        </div>

        {/* Emoji picker */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: Ff.display, fontSize: 16, color: C.black, marginBottom: 10, letterSpacing: 1 }}>
            TON EMOJI ({AVATARS_UNIQUE.length} disponibles)
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 180, overflowY: "auto", padding: 4 }}>
            {AVATARS_UNIQUE.map(a => {
              const thisTaken = existingCombos.includes(`${a}:${colorKey}`);
              return (
                <div key={a} onClick={() => setAvatar(a)} style={{
                  fontSize: 24, width: 44, height: 44,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  borderRadius: "50%", cursor: "pointer",
                  background: avatar === a
                    ? `radial-gradient(circle, ${C.yellow}, ${C.yellowDark})`
                    : `radial-gradient(circle, ${C.chrome}, ${C.chromeShine})`,
                  border: `2.5px solid ${avatar === a ? C.black : thisTaken ? C.red + "88" : C.chromeDark}`,
                  boxShadow: avatar === a ? `0 0 10px ${C.yellow}88` : "none",
                  opacity: thisTaken && avatar !== a ? 0.45 : 1,
                  transition: "all 0.15s",
                  position: "relative",
                }}>{a}</div>
              );
            })}
          </div>
        </div>

        {/* Color picker */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontFamily: Ff.display, fontSize: 16, color: C.black, marginBottom: 10, letterSpacing: 1 }}>
            COULEUR DE FOND
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {AVATAR_COLORS.map(col => {
              const thisTaken = existingCombos.includes(`${avatar}:${col.key}`);
              return (
                <div key={col.key} onClick={() => !thisTaken && setColorKey(col.key)}
                  title={thisTaken ? `${col.label} — déjà pris avec cet emoji` : col.label}
                  style={{
                    width: 36, height: 36, borderRadius: "50%", cursor: thisTaken ? "not-allowed" : "pointer",
                    background: `radial-gradient(circle at 35% 30%, ${col.light}, ${col.bg})`,
                    border: `3px solid ${colorKey === col.key ? C.black : "transparent"}`,
                    boxShadow: colorKey === col.key ? `0 0 10px ${col.bg}99` : "none",
                    opacity: thisTaken ? 0.3 : 1,
                    transition: "all 0.15s",
                    position: "relative",
                  }}>
                  {colorKey === col.key && (
                    <div style={{
                      position: "absolute", inset: 0, display: "flex",
                      alignItems: "center", justifyContent: "center",
                      fontSize: 16, color: "#FFF", textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                    }}>✓</div>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ fontFamily: Ff.body, fontSize: 10, color: C.steel, marginTop: 6 }}>
            Les combinaisons grisées sont déjà utilisées dans ta classe — {AVATARS_UNIQUE.length * AVATAR_COLORS.length} combinaisons possibles au total
          </div>
        </div>

        <div style={{ textAlign: "center" }}>
          <BtnPrimary
            onClick={() => {
              if (!canSubmit) return;
              onComplete({ pseudo: pseudo.trim(), avatar, avatarColor: colorKey, sessions: {} });
            }}
            disabled={!canSubmit}
          >
            🎮 COMMENCER LE MOOC
          </BtnPrimary>
        </div>

        <div style={{
          marginTop: 20, textAlign: "center",
          fontFamily: Ff.body, fontSize: 10, color: C.steel, letterSpacing: 2,
        }}>
          ◆ INSERT COIN TO CONTINUE ◆
        </div>
      </ChromePanel>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SESSION LIST — HOME
// ═══════════════════════════════════════════════════════════════
function SessionCard({ session, sessionData, onClick }) {
  const [hov, setHov] = useState(false);
  const juniorDone = sessionData?.juniorValidated;
  const seniorDone = sessionData?.seniorValidated;
  const juniorScore = sessionData?.juniorScore;
  const seniorScore = sessionData?.seniorScore;
  const pct = ((juniorDone ? 30 : 0) + (seniorDone ? 70 : 0));

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={onClick}
      style={{
        background: hov ? `linear-gradient(135deg, ${C.yellow}22, ${C.ivory})` : C.ivory,
        border: `2px solid ${juniorDone && seniorDone ? C.green : juniorDone ? C.blue : C.chromeDark}`,
        borderRadius: 12, padding: 16, cursor: "pointer",
        boxShadow: hov ? `0 6px 20px ${session.color}33` : "0 2px 6px rgba(0,0,0,0.08)",
        transition: "all 0.2s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
        <div style={{
          fontSize: 28, width: 50, height: 50,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `${session.color}22`, border: `2px solid ${session.color}`,
          borderRadius: 10,
        }}>{session.emoji}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: Ff.display, fontSize: 18, color: C.black, letterSpacing: 1 }}>
            S{session.num} — {session.title}
          </div>
          <div style={{ fontFamily: Ff.body, fontSize: 12, color: C.steel }}>{session.desc}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{
            fontFamily: Ff.mono, fontSize: 22, color: session.color,
            textShadow: `0 0 6px ${session.color}44`,
          }}>{pct}</div>
          <div style={{ fontFamily: Ff.body, fontSize: 9, color: C.steel }}>/ 100</div>
        </div>
      </div>

      {/* Level badges */}
      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        <span style={{
          fontFamily: Ff.body, fontWeight: 700, fontSize: 10, letterSpacing: 1,
          padding: "2px 8px", borderRadius: 10,
          background: juniorDone ? C.blue : C.chrome,
          color: juniorDone ? "#FFF" : C.steel,
          border: `1.5px solid ${juniorDone ? C.blue : C.chromeDark}`,
        }}>
          {juniorDone ? `✓ JUNIOR ${juniorScore}/100` : "○ JUNIOR"}
        </span>
        <span style={{
          fontFamily: Ff.body, fontWeight: 700, fontSize: 10, letterSpacing: 1,
          padding: "2px 8px", borderRadius: 10,
          background: seniorDone ? C.red : C.chrome,
          color: seniorDone ? "#FFF" : C.steel,
          border: `1.5px solid ${seniorDone ? C.red : C.chromeDark}`,
          opacity: !juniorDone ? 0.5 : 1,
        }}>
          {seniorDone ? `✓ SENIOR ${seniorScore}/100` : juniorDone ? "● SENIOR à faire" : "○ SENIOR (déblocage)"}
        </span>
      </div>

      <div style={{ height: 5, background: C.chrome, borderRadius: 3, overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${pct}%`,
          background: `linear-gradient(90deg, ${session.color}, ${C.yellow})`,
          borderRadius: 3, transition: "width 0.8s ease",
          boxShadow: pct > 0 ? `0 0 6px ${session.color}66` : "none",
        }} />
      </div>
    </div>
  );
}

function HomeView({ profile, setView, setCurrentSession }) {
  const totalScore = computeScore(profile.sessions);
  const badges = computeBadges(profile);
  const progress = Math.round((totalScore / MAX_SCORE) * 100);

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: "24px 16px" }}>
      {/* Player panel */}
      <ChromePanel style={{ marginBottom: 24, display: "flex", alignItems: "center", gap: 16 }}>
        <AvatarBubble emoji={profile.avatar} colorKey={profile.avatarColor || "blue"} size={64} fontSize={36} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: Ff.display, fontSize: 24, color: C.black, letterSpacing: 1 }}>
            {profile.pseudo}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            {badges.length > 0 ? badges.map((b, i) => (
              <span key={i} style={{ fontSize: 18 }}>{b}</span>
            )) : <span style={{ fontFamily: Ff.body, fontSize: 12, color: C.steel }}>Aucun badge encore — à toi de jouer !</span>}
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <SevenSeg value={totalScore} digits={3} size={24} />
          <div style={{ fontFamily: Ff.body, fontSize: 10, color: C.steel, letterSpacing: 1, marginTop: 2 }}>/ {MAX_SCORE} PTS</div>
        </div>
      </ChromePanel>

      {/* Progress bar global */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontFamily: Ff.display, fontSize: 14, color: C.black, letterSpacing: 1 }}>PROGRESSION GLOBALE</span>
          <span style={{ fontFamily: Ff.mono, fontSize: 14, color: C.blue }}>{progress}%</span>
        </div>
        <div style={{ height: 12, background: C.chrome, borderRadius: 6, overflow: "hidden", border: `1.5px solid ${C.chromeDark}` }}>
          <div style={{
            height: "100%", width: `${progress}%`,
            background: `linear-gradient(90deg, ${C.red}, ${C.yellow})`,
            borderRadius: 6, transition: "width 1s ease",
            boxShadow: `0 0 10px ${C.yellow}88`,
          }} />
        </div>
      </div>

      {/* Téléphone banner */}
      <div style={{
        background: `linear-gradient(90deg, ${C.blue}EE, ${C.red}EE)`,
        borderRadius: 8, padding: "8px 16px", marginBottom: 24,
        display: "flex", alignItems: "center", gap: 12,
        border: `2px solid ${C.black}`,
      }}>
        <span style={{ fontFamily: Ff.display, fontSize: 13, color: C.yellow, letterSpacing: 2 }}>
          🎵 TÉLÉPHONE — "ON TE DONNE 3 BALLES" (1979)
        </span>
        <span style={{ fontFamily: Ff.body, fontSize: 11, color: "#FFF", fontStyle: "italic", marginLeft: "auto" }}>
          "...it's more fun to compete !"
        </span>
      </div>

      {/* Sessions grid */}
      <div style={{ display: "grid", gap: 12 }}>
        {SESSIONS.map(s => (
          <SessionCard
            key={s.id}
            session={s}
            sessionData={profile.sessions[s.id]}
            onClick={() => { setCurrentSession(s.id); setView("session"); }}
          />
        ))}
      </div>

      <div style={{
        marginTop: 24, textAlign: "center",
        fontFamily: Ff.body, fontSize: 10, color: C.chromeDark, letterSpacing: 3,
      }}>◆ INSERT COIN TO CONTINUE ◆ BTS COMMUNICATION ◆</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// QUIZ COMPONENT (with 3-balles)
// ═══════════════════════════════════════════════════════════════
function QuizComponent({ questions, onComplete, onScoreUpdate, existingAttempts = 0, existingScore = null }) {
  const MAX_ATTEMPTS = 3;
  const attemptsLeft = Math.max(0, MAX_ATTEMPTS - existingAttempts);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(null);

  if (existingAttempts >= MAX_ATTEMPTS && existingScore !== null && existingScore < 75) {
    return (
      <div style={{ textAlign: "center", padding: 20 }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>💀</div>
        <div style={{ fontFamily: Ff.display, fontSize: 22, color: C.red }}>GAME OVER</div>
        <div style={{ fontFamily: Ff.mono, fontSize: 36, color: C.red, margin: "8px 0",
          textShadow: `0 0 10px ${C.red}66` }}>
          {existingScore}/100
        </div>
        <div style={{ fontFamily: Ff.body, fontSize: 13, color: C.black, marginBottom: 4 }}>
          Ton meilleur score sur 3 tentatives
        </div>
        <div style={{ fontFamily: Ff.body, fontSize: 12, color: C.steel }}>
          Contacte ton enseignant pour réinitialiser les balles.
        </div>
        <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
          <BallsIndicator remaining={0} />
        </div>
      </div>
    );
  }

  if (!submitted) {
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontFamily: Ff.body, fontSize: 12, color: C.steel }}>
            {Object.keys(answers).length} / {questions.length} réponses
          </div>
          <BallsIndicator remaining={attemptsLeft} />
        </div>

        {existingScore !== null && (
          <div style={{
            background: `${C.yellow}22`, border: `2px solid ${C.yellow}`,
            borderRadius: 8, padding: "8px 14px", marginBottom: 16,
            fontFamily: Ff.body, fontSize: 13, color: C.black,
          }}>
            Meilleur score : <strong>{existingScore}/100</strong> — Tentative {existingAttempts + 1}/{MAX_ATTEMPTS}
          </div>
        )}

        {questions.map((q, qi) => (
          <div key={qi} style={{
            marginBottom: 14, padding: 14,
            border: `2px solid ${answers[qi] !== undefined ? C.blue : C.chromeDark}`,
            borderRadius: 10, background: answers[qi] !== undefined ? `${C.blue}08` : C.chrome + "44",
          }}>
            <div style={{ fontFamily: Ff.body, fontWeight: 700, fontSize: 14, color: C.black, marginBottom: 10 }}>
              {qi + 1}. {q.q}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {q.choices.map((ch, ci) => (
                <div key={ci} onClick={() => setAnswers(a => ({ ...a, [qi]: ci }))} style={{
                  fontFamily: Ff.body, fontSize: 13, color: C.black,
                  padding: "8px 12px", borderRadius: 8, cursor: "pointer",
                  background: answers[qi] === ci ? `linear-gradient(135deg, ${C.blue}22, ${C.blue}08)` : "#FFF",
                  border: `2px solid ${answers[qi] === ci ? C.blue : C.chromeDark}`,
                  boxShadow: answers[qi] === ci ? `0 0 8px ${C.blue}33` : "none",
                  transition: "all 0.15s",
                }}>{ch}</div>
              ))}
            </div>
          </div>
        ))}

        <div style={{ textAlign: "center", marginTop: 16 }}>
          <BtnPrimary
            onClick={() => {
              const sc = Math.round((questions.filter((q, i) => answers[i] === q.correct).length / questions.length) * 100);
              setScore(sc);
              setSubmitted(true);
              if (onScoreUpdate) onScoreUpdate(sc, (existingAttempts || 0) + 1);
            }}
            disabled={Object.keys(answers).length < questions.length}
          >
            VALIDER ({Object.keys(answers).length}/{questions.length})
          </BtnPrimary>
        </div>
      </div>
    );
  }

  // Results
  const correct = questions.filter((q, i) => answers[i] === q.correct).length;
  const passed = score >= 75;
  return (
    <div>
      <div style={{
        textAlign: "center", padding: 20, marginBottom: 16,
        background: passed ? `${C.green}11` : `${C.red}11`,
        border: `3px solid ${passed ? C.green : C.red}`,
        borderRadius: 12,
      }}>
        <div style={{ fontSize: 40 }}>{passed ? "🎯" : "💥"}</div>
        <div style={{ fontFamily: Ff.display, fontSize: 28, color: passed ? C.green : C.red }}>
          {passed ? "VALIDÉ !" : "RATÉ..."}
        </div>
        <div style={{ fontFamily: Ff.mono, fontSize: 36, color: passed ? C.green : C.red, margin: "8px 0" }}>
          {score}/100
        </div>
        <div style={{ fontFamily: Ff.body, fontSize: 13, color: C.black }}>
          {correct}/{questions.length} bonnes réponses
        </div>
        <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
          <BallsIndicator remaining={attemptsLeft - 1} />
        </div>
      </div>

      {/* Answer review */}
      {questions.map((q, qi) => {
        const ok = answers[qi] === q.correct;
        return (
          <div key={qi} style={{
            marginBottom: 8, padding: "10px 14px",
            border: `2px solid ${ok ? C.green : C.red}`,
            borderRadius: 8,
            background: ok ? `${C.green}08` : `${C.red}08`,
          }}>
            <div style={{ fontFamily: Ff.body, fontWeight: 700, fontSize: 12, color: C.black }}>
              {ok ? "✓" : "✗"} {qi + 1}. {q.q}
            </div>
            {!ok && (
              <div style={{ fontFamily: Ff.body, fontSize: 11, color: C.green, marginTop: 4 }}>
                → Bonne réponse : {q.choices[q.correct]}
              </div>
            )}
          </div>
        );
      })}

      <div style={{ textAlign: "center", marginTop: 16 }}>
        <BtnPrimary onClick={() => onComplete(score, existingAttempts + 1)}>
          {passed ? "CONTINUER →" : attemptsLeft > 1 ? "RÉESSAYER" : "TERMINER"}
        </BtnPrimary>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// RENDU COMPONENT (qualitative submission + AI eval)
// ═══════════════════════════════════════════════════════════════
function RenduComponent({ sessionTitle, level, renduConfig, sessionId, onComplete, existingResult }) {
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [audioBlob, setAudioBlob] = useState(null);
  const [recording, setRecording] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [result, setResult] = useState(existingResult || null);
  const [appealMode, setAppealMode] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const isAudio = renduConfig.type === "audio";

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRef.current = new MediaRecorder(stream);
      chunksRef.current = [];
      mediaRef.current.ondataavailable = e => chunksRef.current.push(e.data);
      mediaRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
      };
      mediaRef.current.start();
      setRecording(true);
    } catch { alert("Microphone non accessible"); }
  };

  const stopRecording = () => {
    if (mediaRef.current) { mediaRef.current.stop(); setRecording(false); }
  };

  const handleSubmit = async () => {
    const submission = isAudio
      ? `[RENDU AUDIO - ${renduConfig.titre}]\n${text || "Pas de notes texte fournies."}`
      : `${text}\n${url ? "Lien : " + url : ""}`;
    if (!submission.trim() || submission.length < 50) {
      alert("Soumission trop courte. Développe ta production."); return;
    }
    setEvaluating(true);
    const evalResult = await evaluateWithAI(sessionTitle, level, renduConfig.criteres, submission, renduConfig.type);
    setEvaluating(false);
    setResult(evalResult);
    onComplete(evalResult);
  };

  if (result && !appealMode) {
    const passed = result.validated;
    return (
      <div>
        <div style={{
          padding: 20, marginBottom: 16, textAlign: "center",
          background: passed ? `${C.green}11` : `${C.red}11`,
          border: `3px solid ${passed ? C.green : C.red}`, borderRadius: 12,
        }}>
          <div style={{ fontSize: 36 }}>{passed ? "✅" : "❌"}</div>
          <div style={{ fontFamily: Ff.display, fontSize: 26, color: passed ? C.green : C.red }}>
            {passed ? "SENIOR VALIDÉ !" : "NON VALIDÉ"}
          </div>
          <div style={{ fontFamily: Ff.mono, fontSize: 32, color: passed ? C.green : C.red, margin: "8px 0" }}>
            {result.total}/100
          </div>
        </div>

        {/* Criteria scores */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: Ff.display, fontSize: 14, color: C.black, marginBottom: 8 }}>DÉTAIL PAR CRITÈRE</div>
          {renduConfig.criteres.map((c, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <div style={{ fontFamily: Ff.body, fontSize: 11, color: C.black, flex: 1 }}>{c}</div>
              <div style={{
                fontFamily: Ff.mono, fontSize: 14,
                color: result.scores[i] >= 14 ? C.green : result.scores[i] >= 10 ? C.blue : C.red,
                minWidth: 40, textAlign: "right",
              }}>{result.scores[i]}/20</div>
              <div style={{
                width: 80, height: 8, background: C.chrome, borderRadius: 4, overflow: "hidden",
              }}>
                <div style={{
                  height: "100%", width: `${(result.scores[i] / 20) * 100}%`,
                  background: result.scores[i] >= 14 ? C.green : result.scores[i] >= 10 ? C.blue : C.red,
                  borderRadius: 4,
                }} />
              </div>
            </div>
          ))}
        </div>

        {/* Feedback */}
        <div style={{
          background: `${C.yellow}22`, border: `2px solid ${C.yellow}`,
          borderRadius: 10, padding: 14, marginBottom: 16,
        }}>
          <div style={{ fontFamily: Ff.display, fontSize: 14, color: C.black, marginBottom: 6 }}>RETOUR DE L'IA</div>
          <div style={{ fontFamily: Ff.body, fontSize: 13, color: C.black, lineHeight: 1.6 }}>
            {result.feedback}
          </div>
          {result.improve && (
            <div style={{ marginTop: 8, fontFamily: Ff.body, fontSize: 12, color: C.red, fontWeight: 600 }}>
              → Axe prioritaire : {result.improve}
            </div>
          )}
        </div>

        {!passed && (
          <div style={{
            background: `${C.blue}11`, border: `2px solid ${C.blue}`,
            borderRadius: 8, padding: 12, marginBottom: 12,
          }}>
            <div style={{ fontFamily: Ff.body, fontSize: 12, color: C.blue, fontWeight: 700, marginBottom: 4 }}>
              ℹ️ Tu peux améliorer ton rendu (tentatives illimitées pour les rendus qualitatifs)
            </div>
            <BtnSecondary onClick={() => { setResult(null); setText(""); setUrl(""); }}>Soumettre une version améliorée</BtnSecondary>
          </div>
        )}

        <div style={{
          background: C.chrome, border: `1px dashed ${C.chromeDark}`,
          borderRadius: 8, padding: 12, marginTop: 8,
        }}>
          <div style={{ fontFamily: Ff.body, fontSize: 11, color: C.steel, marginBottom: 6 }}>
            Tu contestes cette évaluation ?
          </div>
          <BtnSecondary onClick={() => setAppealMode(true)}>
            ⚖️ Faire appel à l'enseignant
          </BtnSecondary>
        </div>
      </div>
    );
  }

  if (appealMode) {
    return (
      <div>
        <div style={{
          background: `${C.orange}22`, border: `2px solid ${C.orange}`,
          borderRadius: 10, padding: 16,
        }}>
          <div style={{ fontFamily: Ff.display, fontSize: 18, color: C.orange, marginBottom: 10 }}>APPEL À L'ENSEIGNANT</div>
          <div style={{ fontFamily: Ff.body, fontSize: 13, color: C.black, lineHeight: 1.6, marginBottom: 12 }}>
            Ta demande d'appel a été enregistrée. L'enseignant recevra une notification dans le panel admin.
            Il pourra réviser ton évaluation (score actuel : <strong>{result?.total}/100</strong>).
          </div>
          <div style={{ fontFamily: Ff.body, fontSize: 11, color: C.steel }}>
            Explique brièvement pourquoi tu contestes (optionnel) :
          </div>
          <textarea rows={3} style={{
            width: "100%", marginTop: 6, fontFamily: Ff.body, fontSize: 13,
            border: `2px solid ${C.chromeDark}`, borderRadius: 8, padding: 10,
            background: C.ivory, color: C.black, resize: "vertical", boxSizing: "border-box",
          }} placeholder="Ex: J'ai bien cité les données chiffrées mais le critère n'a pas été pris en compte..." />
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <BtnPrimary onClick={() => setAppealMode(false)}>Envoyer la demande</BtnPrimary>
            <BtnSecondary onClick={() => setAppealMode(false)}>Annuler</BtnSecondary>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        background: `${C.blue}11`, border: `2px solid ${C.blue}`,
        borderRadius: 10, padding: 14, marginBottom: 16,
      }}>
        <div style={{ fontFamily: Ff.display, fontSize: 15, color: C.blue, marginBottom: 6 }}>CRITÈRES D'ÉVALUATION (IA)</div>
        {renduConfig.criteres.map((c, i) => (
          <div key={i} style={{ fontFamily: Ff.body, fontSize: 12, color: C.black, marginBottom: 3 }}>
            {i + 1}. {c} <span style={{ color: C.steel }}>(/ 20)</span>
          </div>
        ))}
        <div style={{ fontFamily: Ff.body, fontSize: 11, color: C.green, marginTop: 6, fontWeight: 700 }}>
          Seuil de validation : 70/100 — Tentatives illimitées
        </div>
      </div>

      {isAudio ? (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: Ff.display, fontSize: 16, color: C.black, marginBottom: 10 }}>
            🎤 ENREGISTREMENT AUDIO (objectif : 2 min)
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
            {!recording ? (
              <BtnPrimary onClick={startRecording}>⏺ DÉMARRER</BtnPrimary>
            ) : (
              <button onClick={stopRecording} style={{
                fontFamily: Ff.display, fontSize: 16, padding: "10px 22px",
                background: `linear-gradient(135deg, ${C.red}, #AA0011)`,
                color: "#FFF", border: `2px solid ${C.black}`,
                borderRadius: 6, cursor: "pointer",
                animation: "pulse 1s infinite",
              }}>⏹ ARRÊTER</button>
            )}
            {audioBlob && (
              <audio controls src={URL.createObjectURL(audioBlob)} style={{ height: 36 }} />
            )}
          </div>
          <style>{`@keyframes pulse { 0%, 100% { box-shadow: 0 0 0 0 ${C.red}66; } 50% { box-shadow: 0 0 0 8px transparent; } }`}</style>
          <div style={{ fontFamily: Ff.body, fontSize: 12, color: C.steel, marginBottom: 8 }}>
            Notes ou transcription (optionnel, améliore l'évaluation) :
          </div>
          <textarea
            value={text} onChange={e => setText(e.target.value)} rows={3}
            placeholder={renduConfig.exemplePrompt}
            style={{ width: "100%", fontFamily: Ff.body, fontSize: 13, padding: 10, border: `2px solid ${C.chromeDark}`, borderRadius: 8, background: C.ivory, color: C.black, resize: "vertical", boxSizing: "border-box" }}
          />
        </div>
      ) : (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: Ff.display, fontSize: 16, color: C.black, marginBottom: 8 }}>
            TA PRODUCTION
          </div>
          {(renduConfig.type === "deck" || renduConfig.type === "infographie") && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontFamily: Ff.body, fontSize: 12, color: C.steel, marginBottom: 4 }}>
                Lien vers ta production (Google Slides, Canva, Genially...) :
              </div>
              <input
                value={url} onChange={e => setUrl(e.target.value)}
                placeholder="https://docs.google.com/presentation/..."
                style={{ width: "100%", fontFamily: Ff.body, fontSize: 13, padding: "8px 12px", border: `2px solid ${C.chromeDark}`, borderRadius: 8, background: C.ivory, color: C.black, boxSizing: "border-box" }}
              />
            </div>
          )}
          <div style={{ fontFamily: Ff.body, fontSize: 12, color: C.steel, marginBottom: 4 }}>
            {renduConfig.type === "ecrit" ? "Ton texte :" : "Description de ta démarche et de tes choix :"}
          </div>
          <textarea
            value={text} onChange={e => setText(e.target.value)}
            rows={renduConfig.type === "ecrit" ? 10 : 6}
            placeholder={renduConfig.exemplePrompt}
            style={{ width: "100%", fontFamily: Ff.body, fontSize: 13, padding: 10, border: `2px solid ${C.chromeDark}`, borderRadius: 8, background: C.ivory, color: C.black, resize: "vertical", boxSizing: "border-box" }}
          />
          {renduConfig.type === "ecrit" && (
            <div style={{ fontFamily: Ff.mono, fontSize: 11, color: text.split(" ").length > 280 ? C.red : C.steel, marginTop: 4, textAlign: "right" }}>
              {text.split(" ").filter(w => w).length} mots
            </div>
          )}
        </div>
      )}

      <div style={{ textAlign: "center" }}>
        {evaluating ? (
          <div style={{ fontFamily: Ff.display, fontSize: 16, color: C.blue }}>
            ⏳ ÉVALUATION EN COURS...
          </div>
        ) : (
          <BtnPrimary
            onClick={handleSubmit}
            disabled={(!text.trim() || text.length < 50) && !audioBlob}
          >
            SOUMETTRE POUR ÉVALUATION IA
          </BtnPrimary>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SESSION DETAIL VIEW
// ═══════════════════════════════════════════════════════════════
function SessionView({ sessionId, profile, onUpdate, setView }) {
  const session = SESSIONS.find(s => s.id === sessionId);
  const [tab, setTab] = useState("contenu"); // contenu | rendu
  const sData = profile.sessions[sessionId] || {};

  const updateSession = (updates) => {
    const newSessions = { ...profile.sessions, [sessionId]: { ...sData, ...updates } };
    const newProfile = { ...profile, sessions: newSessions };
    onUpdate(newProfile);
    pushToLeaderboard(newProfile);
  };

  const juniorValidated = sData.juniorValidated;
  const seniorValidated = sData.seniorValidated;

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: "20px 16px" }}>
      {/* Back */}
      <button onClick={() => setView("home")} style={{
        fontFamily: Ff.body, fontSize: 13, fontWeight: 700, letterSpacing: 1,
        background: "transparent", border: `2px solid ${C.chromeDark}`,
        borderRadius: 6, padding: "6px 14px", cursor: "pointer", marginBottom: 16,
        color: C.black,
      }}>← RETOUR SÉANCES</button>

      {/* Session header */}
      <ChromePanel style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
          <div style={{
            fontSize: 40, width: 60, height: 60,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: `${session.color}22`, border: `3px solid ${session.color}`,
            borderRadius: 12,
          }}>{session.emoji}</div>
          <div>
            <div style={{ fontFamily: Ff.display, fontSize: 26, color: C.black, letterSpacing: 1 }}>
              S{session.num} — {session.title}
            </div>
            <div style={{ fontFamily: Ff.body, fontSize: 13, color: C.steel }}>{session.desc}</div>
          </div>
        </div>

        {/* Level status badges */}
        <div style={{ display: "flex", gap: 8 }}>
          <span style={{ background: juniorValidated ? C.blue : C.chrome, color: juniorValidated ? "#FFF" : C.steel, fontFamily: Ff.body, fontWeight: 700, fontSize: 11, padding: "4px 12px", borderRadius: 10, border: `1.5px solid ${juniorValidated ? C.blue : C.chromeDark}` }}>
            🎯 JUNIOR {sData.juniorScore != null ? `— ${sData.juniorScore}/100` : ""} {juniorValidated ? "✓" : ""}
          </span>
          <span style={{ background: seniorValidated ? C.red : C.chrome, color: seniorValidated ? "#FFF" : C.steel, fontFamily: Ff.body, fontWeight: 700, fontSize: 11, padding: "4px 12px", borderRadius: 10, border: `1.5px solid ${seniorValidated ? C.red : C.chromeDark}` }}>
            🔥 SENIOR {sData.seniorScore != null ? `— ${sData.seniorScore}/100` : ""} {seniorValidated ? "✓" : ""}
          </span>
        </div>
      </ChromePanel>

      {/* Content tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: -2 }}>
        {["contenu", "rendu"].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            fontFamily: Ff.display, fontSize: 14, letterSpacing: 1,
            padding: "8px 18px", border: `2px solid ${C.black}`,
            borderBottom: tab === t ? `2px solid ${C.ivory}` : `2px solid ${C.black}`,
            borderRadius: "8px 8px 0 0",
            background: tab === t ? C.ivory : C.chrome,
            color: C.black, cursor: "pointer",
          }}>
            {t === "contenu" ? "📖 COURS" : "🎯 JUNIOR + 🔥 SENIOR"}
          </button>
        ))}
      </div>

      <div style={{
        background: C.ivory, border: `2px solid ${C.black}`,
        borderRadius: "0 10px 10px 10px", padding: 20,
        boxShadow: "4px 4px 0 rgba(0,0,0,0.1)",
      }}>

        {tab === "contenu" && (
          <div>
            {/* Key figures */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
              {session.content.chiffres.map((fig, i) => (
                <div key={i} style={{
                  flex: "1 1 120px", textAlign: "center", padding: "10px 14px",
                  background: `${session.color}11`, border: `2px solid ${session.color}`,
                  borderRadius: 10,
                }}>
                  <div style={{ fontFamily: Ff.display, fontSize: 22, color: session.color }}>{fig.val}</div>
                  <div style={{ fontFamily: Ff.body, fontSize: 11, color: C.steel }}>{fig.label}</div>
                </div>
              ))}
            </div>

            {session.content.blocs.map((bloc, i) => (
              <div key={i} style={{
                marginBottom: 16, padding: 16,
                border: `2px solid ${C.chromeDark}`, borderRadius: 10,
                background: C.chrome + "44",
              }}>
                <div style={{ fontFamily: Ff.display, fontSize: 18, color: C.black, marginBottom: 8, letterSpacing: 1 }}>
                  {bloc.titre}
                </div>
                <div style={{ fontFamily: Ff.body, fontSize: 13, color: C.black, lineHeight: 1.7, marginBottom: 10 }}>
                  {bloc.texte}
                </div>
                <a href={bloc.url} target="_blank" rel="noopener noreferrer" style={{
                  fontFamily: Ff.body, fontSize: 10, color: C.blue, textDecoration: "underline",
                }}>
                  📎 {bloc.source}
                </a>
              </div>
            ))}
          </div>
        )}

        {tab === "rendu" && (
          <div>
            {/* ── NIVEAU JUNIOR ── */}
            <div style={{
              border: `3px solid ${C.blue}`, borderRadius: 12,
              marginBottom: 24, overflow: "hidden",
            }}>
              <div style={{
                background: `linear-gradient(135deg, ${C.blue}, #003399)`,
                padding: "10px 16px",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div style={{ fontFamily: Ff.display, fontSize: 18, color: "#FFF", letterSpacing: 1 }}>
                  🎯 JUNIOR — {session.junior.titre}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {sData.juniorScore != null && (
                    <span style={{
                      fontFamily: Ff.mono, fontSize: 16, color: "#FFF",
                      background: "rgba(0,0,0,0.3)", padding: "2px 10px", borderRadius: 8,
                    }}>{sData.juniorScore}/100</span>
                  )}
                  {juniorValidated && <span style={{ background: C.yellow, color: C.black, fontFamily: Ff.body, fontWeight: 700, fontSize: 11, padding: "2px 8px", borderRadius: 10 }}>✓ VALIDÉ</span>}
                </div>
              </div>
              <div style={{ padding: 16, background: `${C.blue}08` }}>
                <div style={{ fontFamily: Ff.body, fontSize: 13, color: C.black, marginBottom: 14, lineHeight: 1.6 }}>
                  {session.junior.consigne}
                </div>
                {session.junior.type === "quiz" || session.junior.type === "patterns" ? (
                  <QuizComponent
                    questions={session.junior.questions}
                    existingAttempts={sData.juniorAttempts || 0}
                    existingScore={sData.juniorScore || null}
                    onScoreUpdate={(score, attempts) => {
                      const passed = score >= 75;
                      updateSession({
                        juniorScore: Math.max(sData.juniorScore || 0, score),
                        juniorAttempts: attempts,
                        juniorValidated: passed || sData.juniorValidated,
                      });
                    }}
                    onComplete={() => {}}
                  />
                ) : (
                  <div style={{ fontFamily: Ff.body, fontSize: 13, color: C.steel }}>
                    {session.id === "s6" ? "→ Lance la simulation chatbot dans l'onglet S6 du MOOC original." : "Rendu spécifique à cette séance."}
                  </div>
                )}
              </div>
            </div>

            {/* ── NIVEAU SENIOR ── */}
            <div style={{
              border: `3px solid ${C.red}`, borderRadius: 12,
              overflow: "hidden",
            }}>
              <div style={{
                background: `linear-gradient(135deg, ${C.red}, #990011)`,
                padding: "10px 16px",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div style={{ fontFamily: Ff.display, fontSize: 18, color: "#FFF", letterSpacing: 1 }}>
                  🔥 SENIOR — {session.senior.titre}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {sData.seniorScore != null && (
                    <span style={{
                      fontFamily: Ff.mono, fontSize: 16, color: "#FFF",
                      background: "rgba(0,0,0,0.3)", padding: "2px 10px", borderRadius: 8,
                    }}>{sData.seniorScore}/100</span>
                  )}
                  {seniorValidated && <span style={{ background: C.yellow, color: C.black, fontFamily: Ff.body, fontWeight: 700, fontSize: 11, padding: "2px 8px", borderRadius: 10 }}>✓ VALIDÉ</span>}
                </div>
              </div>
              <div style={{ padding: 16, background: `${C.red}08` }}>
                <div style={{ fontFamily: Ff.body, fontSize: 13, color: C.black, marginBottom: 14, lineHeight: 1.6 }}>
                  {session.senior.consigne}
                </div>
                <RenduComponent
                  sessionTitle={session.title}
                  level="senior"
                  renduConfig={session.senior}
                  sessionId={session.id}
                  existingResult={sData.seniorResult || null}
                  onComplete={(evalResult) => {
                    updateSession({
                      seniorScore: evalResult.total,
                      seniorValidated: evalResult.validated,
                      seniorResult: evalResult,
                      seniorType: session.senior.type,
                    });
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PINBALL ROOM — "IT'S MORE FUN TO COMPETE"
// ═══════════════════════════════════════════════════════════════
function BumperLight({ color, style = {} }) {
  const [lit, setLit] = useState(false);
  useEffect(() => {
    const i = setInterval(() => setLit(v => !v), 700 + Math.random() * 1400);
    return () => clearInterval(i);
  }, []);
  return (
    <div style={{
      width: 36, height: 36, borderRadius: "50%",
      background: lit ? `radial-gradient(circle at 35% 30%, ${color}FF, ${color}77)` : `radial-gradient(circle at 35% 30%, ${color}33, ${color}11)`,
      border: `2.5px solid ${color}`,
      boxShadow: lit ? `0 0 14px 5px ${color}88, inset 0 0 6px ${color}55` : "none",
      transition: "all 0.2s ease",
      ...style,
    }} />
  );
}

function PinballRoom({ myProfile }) {
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roomTab, setRoomTab] = useState("classement");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const data = await loadLeaderboard();
      if (data.length === 0 && myProfile) {
        setBoard([{
          pseudo: myProfile.pseudo, avatar: myProfile.avatar, avatarColor: myProfile.avatarColor || "blue",
          score: computeScore(myProfile.sessions),
          badges: computeBadges(myProfile),
          sessions: myProfile.sessions,
        }]);
      } else setBoard(data);
      setLoading(false);
    };
    load();
  }, [myProfile]);

  const [flash, setFlash] = useState(false);
  useEffect(() => {
    const i = setInterval(() => setFlash(v => !v), 1100);
    return () => clearInterval(i);
  }, []);

  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: "0 16px 24px" }}>

      {/* Bumper row */}
      <div style={{
        position: "relative", height: 60,
        background: `linear-gradient(180deg, ${C.chrome}99, transparent)`,
        borderBottom: `2px dashed ${C.chromeDark}`,
        display: "flex", alignItems: "center", justifyContent: "space-around",
        padding: "0 20px", marginBottom: 16,
        overflow: "hidden",
      }}>
        {[C.red, C.yellow, C.blue, C.green, C.orange, C.red, C.blue].map((col, i) => (
          <BumperLight key={i} color={col} />
        ))}
        {/* Decorative lines */}
        {[20, 35, 50, 65, 80].map((x, i) => (
          <div key={i} style={{ position: "absolute", left: `${x}%`, top: 0, bottom: 0, width: 1, background: `${C.chromeDark}33` }} />
        ))}
      </div>

      {/* Téléphone banner */}
      <div style={{
        background: `linear-gradient(90deg, ${C.blue}EE, ${C.red}EE)`,
        borderRadius: 8, padding: "7px 14px", marginBottom: 16,
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        border: `2px solid ${C.black}`,
      }}>
        <span style={{ fontFamily: Ff.display, fontSize: 12, color: C.yellow, letterSpacing: 2 }}>
          🎵 TÉLÉPHONE — "ON TE DONNE 3 BALLES" (1979)
        </span>
        <span style={{ fontFamily: Ff.body, fontSize: 10, color: "#FFF", fontStyle: "italic", marginLeft: "auto" }}>
          "...it's more fun to compete !"
        </span>
      </div>

      {/* Room tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: -2 }}>
        {[
          { key: "classement", label: "🏆 CLASSEMENT" },
          { key: "sessions", label: "📊 PAR SÉANCE" },
          { key: "badges", label: "🎖 BADGES" },
        ].map(t => (
          <button key={t.key} onClick={() => setRoomTab(t.key)} style={{
            fontFamily: Ff.display, fontSize: 13, letterSpacing: 1,
            padding: "7px 14px", border: `2px solid ${C.black}`,
            borderBottom: roomTab === t.key ? `2px solid ${C.ivory}` : `2px solid ${C.black}`,
            borderRadius: "6px 6px 0 0",
            background: roomTab === t.key ? C.ivory : C.chrome,
            color: C.black, cursor: "pointer",
          }}>{t.label}</button>
        ))}
      </div>

      <div style={{
        background: C.ivory, border: `2px solid ${C.black}`,
        borderRadius: "0 10px 10px 10px", padding: 20,
        boxShadow: "4px 4px 0 rgba(0,0,0,0.08)",
      }}>

        {loading && (
          <div style={{ textAlign: "center", padding: 30, fontFamily: Ff.display, fontSize: 20, color: C.blue }}>
            CHARGEMENT DU TABLEAU...
          </div>
        )}

        {!loading && roomTab === "classement" && (
          <div>
            {board.length === 0 ? (
              <div style={{ textAlign: "center", padding: 30 }}>
                <div style={{ fontSize: 40 }}>🎮</div>
                <div style={{ fontFamily: Ff.display, fontSize: 20, color: C.steel, marginTop: 8 }}>
                  Sois le premier à rejoindre la salle !
                </div>
              </div>
            ) : board.map((entry, i) => {
              const isMe = myProfile && entry.pseudo === myProfile.pseudo;
              return (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "12px 14px", marginBottom: 8,
                  border: isMe ? `3px solid ${C.yellow}` : `2px solid ${C.chromeDark}`,
                  borderRadius: 10,
                  background: isMe ? `${C.yellow}22` : C.ivory,
                  boxShadow: isMe ? `0 2px 10px ${C.yellow}44` : "0 2px 4px rgba(0,0,0,0.06)",
                }}>
                  <div style={{ fontFamily: Ff.body, fontWeight: 700, fontSize: i < 3 ? 22 : 14, minWidth: 36, textAlign: "center" }}>
                    {["🥇", "🥈", "🥉"][i] || `#${i + 1}`}
                  </div>
                  <AvatarBubble emoji={entry.avatar} colorKey={entry.avatarColor || "blue"} size={44} fontSize={24} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: Ff.display, fontSize: 18, color: C.black }}>
                      {entry.pseudo} {isMe && <span style={{ fontSize: 11, color: C.blue, fontFamily: Ff.body }}>(toi)</span>}
                    </div>
                    <div style={{ fontSize: 14 }}>{(entry.badges || []).join(" ")}</div>
                  </div>
                  <div>
                    <div style={{ fontFamily: Ff.mono, fontSize: 22, color: entry.score >= 400 ? C.green : entry.score >= 200 ? C.blue : C.red, textAlign: "right" }}>
                      {entry.score}
                    </div>
                    <div style={{ fontFamily: Ff.body, fontSize: 9, color: C.steel }}>/ 600</div>
                  </div>
                  <div style={{ width: 80 }}>
                    <div style={{ height: 6, background: C.chrome, borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${(entry.score / MAX_SCORE) * 100}%`, background: `linear-gradient(90deg, ${C.red}, ${C.yellow})`, borderRadius: 3 }} />
                    </div>
                    <div style={{ fontFamily: Ff.body, fontSize: 9, color: C.steel, textAlign: "right", marginTop: 2 }}>
                      {Math.round((entry.score / MAX_SCORE) * 100)}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && roomTab === "sessions" && (
          <div>
            {SESSIONS.map((s, si) => (
              <div key={s.id} style={{ marginBottom: 16, padding: 14, border: `2px solid ${s.color}44`, borderRadius: 10, background: `${s.color}08` }}>
                <div style={{ fontFamily: Ff.display, fontSize: 16, color: C.black, marginBottom: 8, display: "flex", justifyContent: "space-between" }}>
                  <span>{s.emoji} S{s.num} — {s.title}</span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <span style={{ background: C.blue, color: "#FFF", fontFamily: Ff.body, fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 10 }}>JUNIOR /30</span>
                    <span style={{ background: C.red, color: "#FFF", fontFamily: Ff.body, fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 10 }}>SENIOR /70</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {board.map((entry, i) => {
                    const sd = entry.sessions?.[s.id] || {};
                    const sc = (sd.juniorValidated ? 30 : 0) + (sd.seniorValidated ? 70 : 0);
                    return (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", gap: 4,
                        background: sc > 0 ? (sc === 100 ? C.green : C.blue) + "22" : C.chrome,
                        border: `1.5px solid ${sc > 0 ? (sc === 100 ? C.green : C.blue) : C.chromeDark}`,
                        borderRadius: 20, padding: "3px 8px",
                        fontFamily: Ff.body, fontSize: 11,
                      }}>
                        <AvatarBubble emoji={entry.avatar} colorKey={entry.avatarColor || "blue"} size={22} fontSize={13} />
                        <span style={{ color: sc > 0 ? (sc === 100 ? C.green : C.blue) : C.steel, fontWeight: 700 }}>
                          {sc > 0 ? sc : "—"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && roomTab === "badges" && (
          <div>
            <div style={{ fontFamily: Ff.display, fontSize: 20, color: C.black, marginBottom: 14 }}>
              HALL OF BADGES
            </div>
            <div style={{ display: "grid", gap: 12 }}>
              {Object.entries(BADGES).map(([key, badge]) => (
                <div key={key} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: 12, border: `2px solid ${C.chromeDark}`,
                  borderRadius: 10, background: C.chrome + "55",
                }}>
                  <div style={{
                    fontSize: 30, width: 54, height: 54,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${C.yellow}, ${C.yellowDark})`,
                    border: `3px solid ${C.black}`,
                    boxShadow: `0 0 10px ${C.yellow}66`,
                  }}>{badge.emoji}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: Ff.display, fontSize: 16, color: C.black }}>{badge.label}</div>
                    <div style={{ fontFamily: Ff.body, fontSize: 12, color: C.steel }}>{badge.desc}</div>
                  </div>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
                    {board.filter(e => (e.badges || []).includes(badge.emoji)).map((e, i) => (
                      <div key={i} title={e.pseudo}>
                        <AvatarBubble emoji={e.avatar} colorKey={e.avatarColor || "blue"} size={30} fontSize={16} />
                      </div>
                    ))}
                    {board.filter(e => (e.badges || []).includes(badge.emoji)).length === 0 && (
                      <span style={{ fontFamily: Ff.body, fontSize: 11, color: C.chromeDark }}>Pas encore décroché</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: 16, textAlign: "center", fontFamily: Ff.body, fontSize: 9, color: C.chromeDark, letterSpacing: 3 }}>
        ◆ INSERT COIN TO CONTINUE ◆ BTS COMMUNICATION ◆ GAMING & PUB ◆
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ADMIN PANEL
// ═══════════════════════════════════════════════════════════════
function AdminPanel({ onClose }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [inputToken, setInputToken] = useState("");
  const [board, setBoard] = useState([]);
  const [editTarget, setEditTarget] = useState(null);
  const [newScore, setNewScore] = useState("");

  useEffect(() => {
    if (authenticated) loadLeaderboard().then(setBoard);
  }, [authenticated]);

  if (!authenticated) {
    return (
      <div style={{ maxWidth: 400, margin: "40px auto", padding: 20 }}>
        <ChromePanel>
          <div style={{ fontFamily: Ff.display, fontSize: 22, color: C.black, marginBottom: 16, textAlign: "center" }}>
            🔒 ACCÈS ENSEIGNANT
          </div>
          <input
            type="password" value={inputToken} onChange={e => setInputToken(e.target.value)}
            placeholder="Code d'accès enseignant"
            style={{ width: "100%", fontFamily: Ff.body, fontSize: 14, padding: "10px 14px", border: `2px solid ${C.chromeDark}`, borderRadius: 8, marginBottom: 12, boxSizing: "border-box" }}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <BtnPrimary onClick={() => {
              if (inputToken === ADMIN_TOKEN) setAuthenticated(true);
              else alert("Code incorrect");
            }}>CONNEXION</BtnPrimary>
            <BtnSecondary onClick={onClose}>Annuler</BtnSecondary>
          </div>
        </ChromePanel>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: 20 }}>
      <ChromePanel>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ fontFamily: Ff.display, fontSize: 24, color: C.black }}>🔑 PANEL ENSEIGNANT</div>
          <BtnSecondary onClick={onClose}>← Fermer</BtnSecondary>
        </div>

        <div style={{
          background: `${C.orange}22`, border: `2px solid ${C.orange}`,
          borderRadius: 8, padding: 12, marginBottom: 16,
          fontFamily: Ff.body, fontSize: 12, color: C.black,
        }}>
          Vous pouvez ici modifier les scores Senior en cas de litige étudiant. Toute modification est irréversible.
        </div>

        {board.map((entry, i) => (
          <div key={i} style={{
            marginBottom: 12, padding: 14,
            border: `2px solid ${C.chromeDark}`, borderRadius: 10,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <AvatarBubble emoji={entry.avatar} colorKey={entry.avatarColor || "blue"} size={36} fontSize={20} />
              <div style={{ fontFamily: Ff.display, fontSize: 18, color: C.black }}>{entry.pseudo}</div>
              <div style={{ fontFamily: Ff.mono, fontSize: 16, color: C.blue, marginLeft: "auto" }}>{entry.score}/600</div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {SESSIONS.map(s => {
                const sd = entry.sessions?.[s.id] || {};
                return (
                  <div key={s.id} style={{
                    padding: "6px 10px", border: `1.5px solid ${C.chromeDark}`,
                    borderRadius: 8, minWidth: 100,
                    background: C.chrome + "55",
                  }}>
                    <div style={{ fontFamily: Ff.display, fontSize: 11, color: C.black, marginBottom: 4 }}>
                      {s.emoji} {s.title}
                    </div>
                    <div style={{ fontFamily: Ff.body, fontSize: 10, color: sd.juniorValidated ? C.blue : C.steel }}>
                      Junior : {sd.juniorScore || "—"} {sd.juniorValidated ? "✓" : ""}
                    </div>
                    <div style={{ fontFamily: Ff.body, fontSize: 10, color: sd.seniorValidated ? C.green : C.steel }}>
                      Senior : {sd.seniorScore || "—"} {sd.seniorValidated ? "✓" : ""}
                    </div>
                    {sd.seniorScore !== undefined && (
                      <button
                        onClick={() => setEditTarget({ pseudo: entry.pseudo, session: s.id, current: sd.seniorScore })}
                        style={{
                          marginTop: 4, fontFamily: Ff.body, fontWeight: 700, fontSize: 9,
                          padding: "2px 6px", background: C.orange, color: "#FFF",
                          border: "none", borderRadius: 4, cursor: "pointer",
                        }}
                      >Modifier</button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {editTarget && (
          <div style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999,
          }}>
            <ChromePanel style={{ maxWidth: 360, width: "90%" }}>
              <div style={{ fontFamily: Ff.display, fontSize: 20, color: C.black, marginBottom: 12 }}>
                MODIFIER SCORE SENIOR
              </div>
              <div style={{ fontFamily: Ff.body, fontSize: 13, color: C.black, marginBottom: 12 }}>
                Étudiant : <strong>{editTarget.pseudo}</strong><br />
                Session : <strong>{editTarget.session}</strong><br />
                Score actuel : <strong>{editTarget.current}/100</strong>
              </div>
              <input
                type="number" min="0" max="100" value={newScore}
                onChange={e => setNewScore(e.target.value)}
                style={{ width: "100%", fontFamily: Ff.mono, fontSize: 20, padding: 10, border: `2px solid ${C.chromeDark}`, borderRadius: 8, marginBottom: 12, boxSizing: "border-box" }}
                placeholder="Nouveau score / 100"
              />
              <div style={{ display: "flex", gap: 8 }}>
                <BtnPrimary onClick={async () => {
                  const sc = parseInt(newScore);
                  if (isNaN(sc) || sc < 0 || sc > 100) return;
                  alert(`Score modifié : ${editTarget.pseudo} / ${editTarget.session} → ${sc}/100\n(Application en base réelle nécessite backend)`);
                  setEditTarget(null); setNewScore("");
                }}>CONFIRMER</BtnPrimary>
                <BtnSecondary onClick={() => { setEditTarget(null); setNewScore(""); }}>Annuler</BtnSecondary>
              </div>
            </ChromePanel>
          </div>
        )}
      </ChromePanel>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// APP ROOT
// ═══════════════════════════════════════════════════════════════
export default function App() {
  const [profile, setProfile] = useState(() => loadProfile());
  const [view, setView] = useState("home");
  const [currentSession, setCurrentSession] = useState(null);
  const [adminMode, setAdminMode] = useState(false);
  const [existingCombos, setExistingCombos] = useState([]);

  // Check for admin URL param
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("admin") === ADMIN_TOKEN) setAdminMode(true);
  }, []);

  // Load existing avatar combos from leaderboard (for doublon detection)
  useEffect(() => {
    if (!profile) {
      loadLeaderboard().then(board => {
        setExistingCombos(board.map(e => `${e.avatar}:${e.avatarColor || "blue"}`));
      });
    }
  }, [profile]);

  const updateProfile = (newProfile) => {
    setProfile(newProfile);
    saveProfile(newProfile);
  };

  const handleOnboarding = (newProfile) => {
    saveProfile(newProfile);
    setProfile(newProfile);
    pushToLeaderboard(newProfile);
  };

  if (adminMode) {
    return (
      <div style={{ minHeight: "100vh", background: C.ivory, fontFamily: Ff.body }}>
        <style>{GOOGLE_FONTS + "* { box-sizing: border-box; } body { margin: 0; }"}</style>
        <div style={{
          background: `linear-gradient(180deg, ${C.chromeDark}, ${C.chrome})`,
          padding: "10px 20px", borderBottom: `3px solid ${C.black}`,
          fontFamily: Ff.display, fontSize: 18, color: C.black,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span>🔑 PANEL ENSEIGNANT — BTS GAMING & PUB</span>
          <button onClick={() => setAdminMode(false)} style={{ background: C.red, color: "#FFF", border: "none", borderRadius: 6, padding: "6px 14px", fontFamily: Ff.body, fontWeight: 700, cursor: "pointer" }}>Fermer</button>
        </div>
        <AdminPanel onClose={() => setAdminMode(false)} />
      </div>
    );
  }

  if (!profile) return (
    <div>
      <style>{GOOGLE_FONTS + "* { box-sizing: border-box; } body { margin: 0; }"}</style>
      <Onboarding onComplete={handleOnboarding} existingCombos={existingCombos} />
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: C.ivory, fontFamily: Ff.body }}>
      <style>{GOOGLE_FONTS + "* { box-sizing: border-box; } body { margin: 0; }"}</style>

      <FlipperHeader currentView={view} setView={setView} profile={profile} />

      {/* Side rails */}
      <div style={{ position: "fixed", left: 0, top: 0, bottom: 0, width: 10, background: `linear-gradient(90deg, ${C.chromeDark}, ${C.chrome})`, borderRight: `2px solid ${C.black}`, zIndex: 150 }} />
      <div style={{ position: "fixed", right: 0, top: 0, bottom: 0, width: 10, background: `linear-gradient(270deg, ${C.chromeDark}, ${C.chrome})`, borderLeft: `2px solid ${C.black}`, zIndex: 150 }} />

      <div style={{ paddingLeft: 10, paddingRight: 10 }}>
        {view === "home" && (
          <HomeView profile={profile} setView={setView} setCurrentSession={setCurrentSession} />
        )}
        {view === "session" && currentSession && (
          <SessionView
            sessionId={currentSession}
            profile={profile}
            onUpdate={updateProfile}
            setView={setView}
          />
        )}
        {view === "room" && (
          <PinballRoom myProfile={profile} />
        )}
      </div>

      {/* Admin access button (discreet) */}
      <div
        onClick={() => setAdminMode(true)}
        title="Accès enseignant"
        style={{
          position: "fixed", bottom: 12, right: 20,
          fontFamily: Ff.body, fontSize: 10, color: C.chromeDark,
          cursor: "pointer", letterSpacing: 1, zIndex: 200,
        }}
      >
        ⚙ admin
      </div>
    </div>
  );
}
